import React, { useState } from 'react'
import { Box, Typography, Chip, Card, CardContent } from '@mui/material'
import {
  Action,
  ActionStatus,
} from '@/features/patient-actions/types/patientActions'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import {
  ACTION_STATUS_LABELS,
  ACTION_GROUP_LABELS,
} from '@/constants/patientConstants'
import { useToast } from '@/shared/contexts/ToastContext'
import {
  CARD_STATUS_STYLES,
  CARD_BASE_STYLES,
} from '@/apps/legacy/shared/constants/cardStyles'

interface ActionCardCompactProps {
  action: Action
  onStatusUpdate?: (id: string, status: ActionStatus) => void
}

const ActionCardCompact: React.FC<ActionCardCompactProps> = ({
  action,
  onStatusUpdate,
}) => {
  const { showToast } = useToast()
  const [currentStatus, setCurrentStatus] = useState(action.status)

  const handleStatusChange = (newStatus: ActionStatus) => {
    if (newStatus === currentStatus) return

    setCurrentStatus(newStatus)
    onStatusUpdate?.(action.id, newStatus)
    showToast(
      `ステータスを「${ACTION_STATUS_LABELS[newStatus]}」に変更しました`,
      'success',
      2000
    )
  }

  const isDone = currentStatus === 'done'
  const isDeclined = currentStatus === 'declined'
  const isInactive = isDone || isDeclined

  return (
    <Card
      sx={[
        {
          mb: 2,
          boxShadow: CARD_BASE_STYLES.boxShadow,
          transition: CARD_BASE_STYLES.transition,
        },
        isDeclined
          ? {
              backgroundColor: CARD_STATUS_STYLES.declined.backgroundColor,
            }
          : {
              backgroundColor: isDone
                ? CARD_STATUS_STYLES.done.backgroundColor
                : CARD_STATUS_STYLES.default.backgroundColor,
            },
        isDone
          ? {
              border: CARD_STATUS_STYLES.done.border,
            }
          : {
              border: isDeclined
                ? CARD_STATUS_STYLES.declined.border
                : CARD_STATUS_STYLES.default.border,
            },
        isDeclined
          ? {
              opacity: CARD_STATUS_STYLES.declined.opacity,
            }
          : {
              opacity: 1,
            },
        isInactive
          ? {
              '&:hover': {
                boxShadow: CARD_BASE_STYLES.inactiveHoverShadow,
              },
            }
          : {
              '&:hover': {
                boxShadow: CARD_BASE_STYLES.hoverShadow,
              },
            },
      ]}
    >
      <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* 左側：コンテンツ */}
          <Box sx={{ flex: 1 }}>
            {/* カードヘッダー */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography
                variant="subtitle1"
                sx={[
                  {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  },
                  isDone
                    ? {
                        color: colorTokens.blue[600],
                      }
                    : {
                        color: colorTokens.text.primary,
                      },
                  isDeclined
                    ? {
                        textDecoration: 'line-through',
                      }
                    : {
                        textDecoration: 'none',
                      },
                ]}
              >
                {isDone && '✓ '}
                {action.label}
              </Typography>
              <Chip
                label={ACTION_GROUP_LABELS[action.group]}
                size="small"
                sx={{
                  height: 18,
                  backgroundColor: colorTokens.action.primaryBgLight,
                  color: colorTokens.blue[600],
                  '& .MuiChip-label': {
                    px: 0.75,
                  },
                }}
              />
            </Box>

            {/* 説明文 */}
            {action.description.length > 0 && (
              <Typography
                variant="body2"
                sx={[
                  {
                    color: colorTokens.text.secondary,
                    lineHeight: 1.6,
                  },
                  isDeclined
                    ? {
                        textDecoration: 'line-through',
                      }
                    : {
                        textDecoration: 'none',
                      },
                ]}
              >
                {action.description.join(' / ')}
              </Typography>
            )}
          </Box>

          {/* 右側：ステータスボタン */}
          {!isInactive && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              {/* 第1列: 進行系ステータス */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  width: 70,
                }}
              >
                {(['pending', 'planned', 'in_progress'] as const).map(
                  (status) => (
                    <Chip
                      key={status}
                      label={ACTION_STATUS_LABELS[status]}
                      size="small"
                      onClick={() => handleStatusChange(status)}
                      sx={[
                        {
                          cursor: 'pointer',
                          height: 24,
                          width: '100%',
                          '&:hover': {
                            borderColor: colorTokens.blue[600],
                          },
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        },
                        currentStatus === status
                          ? {
                              backgroundColor: colorTokens.blue[50],
                            }
                          : {
                              backgroundColor: colorTokens.background.paper,
                            },
                        currentStatus === status
                          ? {
                              color: colorTokens.blue[600],
                            }
                          : {
                              color: colorTokens.text.secondary,
                            },
                        currentStatus === status
                          ? {
                              border: {
                                border: colorTokens.blue[600],
                              },
                            }
                          : {
                              border: {
                                border: colorTokens.border.light,
                              },
                            },
                        currentStatus === status
                          ? {
                              '&:hover': {
                                backgroundColor: colorTokens.blue[100],
                              },
                            }
                          : {
                              '&:hover': {
                                backgroundColor: colorTokens.grey[100],
                              },
                            },
                      ]}
                    />
                  )
                )}
              </Box>

              {/* 区切り線 */}
              <Box
                sx={{
                  borderLeft: `1px solid ${colorTokens.border.light}`,
                  mx: 0.25,
                }}
              />

              {/* 第2列: 完了系ステータス */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  width: 70,
                }}
              >
                {(['done', 'declined'] as const).map((status) => {
                  const isCurrentStatus =
                    currentStatus === (status as ActionStatus)
                  return (
                    <Chip
                      key={status}
                      label={ACTION_STATUS_LABELS[status]}
                      size="small"
                      onClick={() => handleStatusChange(status)}
                      sx={[
                        {
                          cursor: 'pointer',
                          height: 24,
                          width: '100%',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        },
                        isCurrentStatus
                          ? {
                              backgroundColor:
                                status === 'done'
                                  ? colorTokens.blue[50]
                                  : colorTokens.status.declinedBg,
                            }
                          : {
                              backgroundColor: colorTokens.background.paper,
                            },
                        isCurrentStatus
                          ? {
                              color:
                                status === 'done'
                                  ? colorTokens.blue[600]
                                  : colorTokens.status.declined,
                            }
                          : {
                              color: colorTokens.text.secondary,
                            },
                        isCurrentStatus
                          ? {
                              border: {
                                border:
                                  status === 'done'
                                    ? colorTokens.blue[600]
                                    : colorTokens.status.declined,
                              },
                            }
                          : {
                              border: {
                                border: colorTokens.border.light,
                              },
                            },
                        isCurrentStatus
                          ? {
                              '&:hover': {
                                backgroundColor:
                                  status === 'done'
                                    ? colorTokens.blue[100]
                                    : colorTokens.status.declinedHover,
                              },
                            }
                          : {
                              '&:hover': {
                                backgroundColor: colorTokens.grey[100],
                              },
                            },
                        status === 'done'
                          ? {
                              '&:hover': {
                                borderColor: colorTokens.blue[600],
                              },
                            }
                          : {
                              '&:hover': {
                                borderColor: colorTokens.status.declined,
                              },
                            },
                      ]}
                    />
                  )
                })}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default ActionCardCompact
